import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgxCurrencyDirective } from 'ngx-currency';
import { EstoqueLoteSerieInlineComponent, LoteEntradaEstoqueModalItem, SerieEntradaEstoqueModalItem } from '../../../../../shared/components/estoque-lote-serie-inline/estoque-lote-serie-inline.component';
import { LoteSaidaEstoqueModalItem } from '../../../../../shared/components/lote-saida-estoque-modal/lote-saida-estoque-modal.component';
import { SerieSaidaEstoqueModalItem } from '../../../../../shared/components/serie-saida-estoque-modal/serie-saida-estoque-modal.component';
import { AlertService, AlertType } from '../../../../../shared/services/alert.service';
import { ControleEstoqueService } from '../../../../../shared/services/apis/produto/controle-estoque.service';
import { LookupService } from '../../../../../shared/services/apis/produto/produto-lookup.service';
import { CriarMovimentacaoManualDiretaRequest } from '../../../../../shared/services/requests/produto/criar-movimentacao-manual-direta.request';
import { TipoOperacaoEstoqueEnum } from '../../../../../shared/enums/produto/tipo-operacao-estoque.enum';
import { MaskService } from '../../../../../shared/services/mask.service';
import { AbntPipe } from '../../../../../shared/pipes/abnt.pipe';

export interface NovaMovimentacaoProdutoDialogData {
  idProduto: number;
  descricaoProduto: string;
  skuProduto?: string;
  isControlaLotes: boolean;
  isControlaSeries: boolean;
  isFracionada: boolean;
  /** Local que abre selecionado: o filtrado na aba ou, com filtro em "todos", o padrão do sistema. */
  idLocalizacaoEstoque: number | null;
}

@Component({
  selector: 'app-nova-movimentacao-produto-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatButtonModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatSelectModule,
    MatTooltipModule,
    NgxCurrencyDirective,
    AbntPipe,
    EstoqueLoteSerieInlineComponent,
  ],
  templateUrl: './nova-movimentacao-produto-dialog.component.html',
  styleUrl: './nova-movimentacao-produto-dialog.component.css',
})
export class NovaMovimentacaoProdutoDialogComponent implements OnInit {
  public isSalvando = false;
  public readonly hoje: Date = new Date();
  public localizacoes: any[] = [];
  public idLocalizacaoEstoque: number | null = null;
  public idTipoMovimentacao: TipoOperacaoEstoqueEnum = TipoOperacaoEstoqueEnum.Entrada;
  public readonly TipoOperacaoEstoqueEnum = TipoOperacaoEstoqueEnum;
  public dataMovimentacao: Date | null = new Date();
  public descricao = '';
  public quantidade = 1;

  public lotesEntrada: LoteEntradaEstoqueModalItem[] = [];
  public seriesEntrada: SerieEntradaEstoqueModalItem[] = [];
  public lotesSaida: LoteSaidaEstoqueModalItem[] = [];
  public seriesSaida: SerieSaidaEstoqueModalItem[] = [];

  // Unidade não fracionada só aceita quantidade inteira (item 19 do BUG#146148) — a validação
  // já existe no backend, aqui é só refletir na máscara de digitação.
  get quantidadeMask() {
    return this.data.isFracionada ? this.maskService.getDynamicDecimalMask() : this.maskService.getIntegerMask();
  }

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: NovaMovimentacaoProdutoDialogData,
    private dialogRef: MatDialogRef<NovaMovimentacaoProdutoDialogComponent>,
    private alertService: AlertService,
    private lookupService: LookupService,
    private controleEstoqueService: ControleEstoqueService,
    private maskService: MaskService,
  ) {}

  public ngOnInit(): void {
    this.idLocalizacaoEstoque = this.data.idLocalizacaoEstoque;

    this.lookupService.getLocalizacaoEstoqueSelect().subscribe({
      next: response => {
        if (!response?.isError) {
          this.localizacoes = response.list ?? [];
        } else {
          this.alertService.show('Aviso', response.errorDescription || 'Não foi possível carregar os locais de estoque.', AlertType.warning);
        }
      },
      error: () => {
        this.alertService.show('Erro', 'Não foi possível carregar os locais de estoque.', AlertType.error);
      },
    });
  }

  public get isSaida(): boolean {
    return this.idTipoMovimentacao === TipoOperacaoEstoqueEnum.Saida;
  }

  public get possuiRastreabilidade(): boolean {
    return this.data.isControlaLotes || this.data.isControlaSeries;
  }

  public get quantidadeRastreada(): number {
    if (this.data.isControlaLotes) {
      return this.isSaida
        ? this.lotesSaida.reduce((total, lote) => total + (lote.quantidadeSaida || 0), 0)
        : this.lotesEntrada.reduce((total, lote) => total + (lote.quantidade || 0), 0);
    }

    if (this.data.isControlaSeries) {
      return this.isSaida ? this.seriesSaida.length : this.seriesEntrada.length;
    }

    return 0;
  }

  /** Proxy entre os 4 arrays (lote/série x entrada/saída) e o componente inline único —
   * a cada momento só um deles está "ativo" conforme data.isControlaLotes/Series e isSaida. */
  public get itensRastreabilidade(): (LoteEntradaEstoqueModalItem | SerieEntradaEstoqueModalItem | LoteSaidaEstoqueModalItem | SerieSaidaEstoqueModalItem)[] {
    if (this.data.isControlaLotes) return this.isSaida ? this.lotesSaida : this.lotesEntrada;
    if (this.data.isControlaSeries) return this.isSaida ? this.seriesSaida : this.seriesEntrada;
    return [];
  }

  public onItensRastreabilidadeChange(itens: (LoteEntradaEstoqueModalItem | SerieEntradaEstoqueModalItem | LoteSaidaEstoqueModalItem | SerieSaidaEstoqueModalItem)[]): void {
    if (this.data.isControlaLotes) {
      if (this.isSaida) this.lotesSaida = itens as LoteSaidaEstoqueModalItem[];
      else this.lotesEntrada = itens as LoteEntradaEstoqueModalItem[];
    } else if (this.data.isControlaSeries) {
      if (this.isSaida) this.seriesSaida = itens as SerieSaidaEstoqueModalItem[];
      else this.seriesEntrada = itens as SerieEntradaEstoqueModalItem[];
    }
  }

  public onTipoMovimentacaoChange(): void {
    this.lotesEntrada = [];
    this.seriesEntrada = [];
    this.lotesSaida = [];
    this.seriesSaida = [];
  }

  public cancelar(): void {
    if (!this.isSalvando) this.dialogRef.close({ confirmed: false });
  }

  public salvar(): void {
    if (!this.validar()) return;

    this.isSalvando = true;
    const request = this.montarRequest();

    this.controleEstoqueService.criarMovimentacaoManual(request).subscribe({
      next: response => {
        this.isSalvando = false;
        if (response?.isError) {
          this.alertService.show('Aviso', response?.errorDescription || 'Não foi possível salvar a movimentação.', AlertType.warning);
          return;
        }

        this.alertService.show('Sucesso', 'Movimentação realizada com sucesso!', AlertType.success);
        this.dialogRef.close({ confirmed: true });
      },
      error: () => {
        this.isSalvando = false;
        this.alertService.show('Erro', 'Não foi possível salvar a movimentação.', AlertType.error);
      },
    });
  }

  private montarRequest(): CriarMovimentacaoManualDiretaRequest {
    return {
      idProduto: this.data.idProduto,
      idLocalizacaoEstoque: this.idLocalizacaoEstoque!,
      idTipoOperacaoEstoque: this.idTipoMovimentacao,
      quantidade: this.quantidade,
      dataMovimentacao: this.dataMovimentacao ? this.formatarData(this.dataMovimentacao) : '',
      descricao: this.descricao.trim() || undefined,
      lotes: this.isSaida
        ? this.lotesSaida.map(lote => ({
            idLote: lote.idLote,
            codigoLote: lote.codigoLote,
            quantidade: lote.quantidadeSaida,
          }))
        : this.lotesEntrada.map(lote => ({
            idLote: lote.idLote,
            codigoLote: lote.codigoLote,
            dataFabricacao: this.formatarDataOpcional(lote.dataFabricacao),
            dataValidade: this.formatarDataOpcional(lote.dataValidade),
            quantidade: lote.quantidade,
          })),
      series: this.isSaida
        ? this.seriesSaida.map(serie => ({ idSerie: serie.idSerie, codigoSerie: serie.codigoSerie }))
        : this.seriesEntrada.map(serie => ({ idSerie: serie.idSerie, codigoSerie: serie.codigoSerie })),
    };
  }

  private validar(): boolean {
    if (!this.dataMovimentacao) {
      this.alertService.show('Atenção', 'Informe a data da movimentação.', AlertType.warning);
      return false;
    }

    if (!this.idLocalizacaoEstoque) {
      this.alertService.show('Atenção', 'Selecione o local de estoque.', AlertType.warning);
      return false;
    }

    if (!(this.quantidade > 0)) {
      this.alertService.show('Atenção', 'Informe uma quantidade maior que zero.', AlertType.warning);
      return false;
    }

    if (!this.isRastreabilidadeValida()) {
      const tipo = this.data.isControlaLotes ? 'lotes' : 'séries';
      this.alertService.show(
        'Atenção',
        `Informe os ${tipo} correspondentes à quantidade da movimentação.`,
        AlertType.warning,
      );
      return false;
    }

    return true;
  }

  private isRastreabilidadeValida(): boolean {
    if (this.data.isControlaLotes) {
      const lotes = this.isSaida ? this.lotesSaida : this.lotesEntrada;
      return lotes.length > 0 && this.quantidadeRastreada === this.quantidade;
    }

    if (this.data.isControlaSeries) {
      const series = this.isSaida ? this.seriesSaida : this.seriesEntrada;
      return series.length > 0 && series.length === this.quantidade;
    }

    return true;
  }

  public onDateInput(event: Event): void {
    MaskService.onDateInput(event);
  }

  public onDateKeydown(event: KeyboardEvent): void {
    const teclasPermitidas = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End', 'Enter'];
    const isAtalho = (event.ctrlKey || event.metaKey) && ['a', 'c', 'v', 'x'].includes(event.key.toLowerCase());
    if (teclasPermitidas.includes(event.key) || isAtalho) return;
    if (!/^\d$/.test(event.key)) event.preventDefault();
  }

  public onDataMovimentacaoBlur(event: FocusEvent): void {
    const input = event.target as HTMLInputElement;
    const data = this.lerDataInput(input);
    if (data && data > this.hojeNormalizado()) {
      input.value = '';
      this.dataMovimentacao = null;
      this.alertService.show('Atenção', 'A data da movimentação não pode ser maior que a data atual.', AlertType.warning);
      return;
    }
    this.dataMovimentacao = data;
  }

  private lerDataInput(input: HTMLInputElement): Date | null {
    const valor = (input?.value || '').trim();
    if (!valor) return null;
    const apenasNumeros = valor.replace(/\D/g, '').substring(0, 8);
    if (apenasNumeros.length !== 8) {
      input.value = '';
      this.alertService.show('Atenção', 'Informe uma data válida no formato DD/MM/AAAA.', AlertType.warning);
      return null;
    }
    const formatado = `${apenasNumeros.substring(0, 2)}/${apenasNumeros.substring(2, 4)}/${apenasNumeros.substring(4, 8)}`;
    input.value = formatado;
    const data = this.parseDataBr(formatado);
    if (!data) {
      input.value = '';
      this.alertService.show('Atenção', 'Informe uma data válida no formato DD/MM/AAAA.', AlertType.warning);
    }
    return data;
  }

  private parseDataBr(valor: string): Date | null {
    const partes = valor.split('/');
    if (partes.length !== 3) return null;
    const dia = Number(partes[0]);
    const mes = Number(partes[1]);
    const ano = Number(partes[2]);
    if (!Number.isInteger(dia) || !Number.isInteger(mes) || !Number.isInteger(ano)) return null;
    if (ano < 1000 || ano > 9999 || mes < 1 || mes > 12 || dia < 1 || dia > 31) return null;
    const data = new Date(ano, mes - 1, dia);
    data.setHours(0, 0, 0, 0);
    if (data.getFullYear() !== ano || data.getMonth() !== mes - 1 || data.getDate() !== dia) return null;
    return data;
  }

  private hojeNormalizado(): Date {
    const h = new Date();
    h.setHours(0, 0, 0, 0);
    return h;
  }

  private formatarData(data: Date): string {
    return `${data.getFullYear()}-${String(data.getMonth() + 1).padStart(2, '0')}-${String(data.getDate()).padStart(2, '0')}`;
  }

  private formatarDataOpcional(valor?: string | Date | null): string | undefined {
    if (!valor) return undefined;
    if (typeof valor === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(valor)) return valor;

    const data = valor instanceof Date ? valor : new Date(valor);
    return Number.isNaN(data.getTime()) ? undefined : this.formatarData(data);
  }
}
